import type { ComponentType } from 'react';
import {
  Bank,
  Book,
  Building,
  Bus,
  Calendar,
  Chart,
  Coffee,
  DocumentText,
  Folder,
  Home2,
  Messages,
  People,
  Profile2User,
  Setting2,
  ShieldTick,
  TaskSquare,
  Teacher,
  UserTick,
  Wallet,
} from 'iconsax-reactjs';
import type { PermissionRequirement } from '@/lib/auth/permissions';

export type IconComponent = ComponentType<{
  size?: number | string;
  color?: string;
  variant?: 'Linear' | 'Outline' | 'Broken' | 'Bold' | 'Bulk' | 'TwoTone';
}>;

export interface AppNavItem {
  id: string;
  label: string;
  /** Route path. Items with children use the first child path as their landing page. */
  path?: string;
  icon?: IconComponent;
  requires?: PermissionRequirement;
  /**
   * 'available' routes render a real screen; 'planned' routes render the module foundation page
   * (the module's permission namespace and navigation exist, its screens ship in a later phase).
   */
  status?: 'available' | 'planned';
  description?: string;
  /**
   * Routed and authorized like any other item, but not shown in the menu (reached through links,
   * e.g. a household from a student's family section).
   */
  hidden?: boolean;
  children?: AppNavItem[];
}

export interface AppNavSection {
  id: string;
  label?: string;
  items: AppNavItem[];
}

const planned = (
  id: string,
  label: string,
  path: string,
  requires: PermissionRequirement,
  description: string,
): AppNavItem => ({ id, label, path, requires, status: 'planned', description });

const available = (
  id: string,
  label: string,
  path: string,
  requires: PermissionRequirement,
  description: string,
): AppNavItem => ({ id, label, path, requires, status: 'available', description });

/** Counselor workspace: counseling is sensitive and needs the base student permission too. */
const GUIDANCE: PermissionRequirement = { allOf: ['student.read', 'counseling.read'] };

/** Front desk reads (manage implies read on the server). */
const FRONT_OFFICE_READ = ['front_office.read', 'front_office.manage'];

/** Teacher workspace: anyone who manages lesson plans or grades (teachers). */
const TEACHER: PermissionRequirement = { anyOf: ['lesson_plan.manage', 'grades.manage'] };

/**
 * Report card administration (whole cards): managers and records staff. Teachers holding only
 * report_card.read work through Teaching → Report Cards (their own sections).
 */
const REPORT_CARD_ADMIN: PermissionRequirement = {
  anyOf: ['report_card.manage', 'transcript.read', 'transcript.manage'],
};

/** GPA policies and graduation requirement sets (read). */
const RECORDS_CONFIG: PermissionRequirement = {
  anyOf: ['transcript.read', 'transcript.manage', 'report_card.manage'],
};

/** Communication Center compose permissions (any of them opens the messages screen). */
const COMMS_COMPOSE = [
  'communications.send',
  'communications.send.class',
  'communications.broadcast',
  'communications.staff',
  'communications.templates.manage',
  'communications.manage',
] as const;

const OPERATION_ICONS: Record<string, IconComponent> = {
  lunch: Coffee,
  communications: Messages,
  records: Folder,
  budgets: Wallet,
  calendar: Calendar,
  reports: Chart,
};

/**
 * Single source of truth for navigation AND route authorization: the router builds guarded routes
 * from this tree, so an item hidden from the menu is also unreachable by URL.
 */
export const NAVIGATION: AppNavSection[] = [
  {
    id: 'home',
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: Home2, status: 'available' },
      {
        // Hosts approve visitors who asked to see them (GET /me/visitor-approvals). There is no
        // "is a host" permission: shown to staff-type users; the list is empty for non-hosts.
        ...available(
          'visitor-approvals',
          'Visitor Approvals',
          '/visitor-approvals',
          { anyOf: ['staff.read', 'class.read', 'student.read', 'visitor.read', 'visitor.manage'] },
          'Visitors waiting for your approval.',
        ),
        icon: UserTick,
      },
    ],
  },
  {
    // The teacher workspace: deliberately simple, today-first screens for the classes a teacher
    // is assigned to. Gradebook and lesson planning live here (they replaced the planned
    // Academics entries); Academics keeps the catalog and scheduling screens for administrators.
    id: 'teaching',
    label: 'Teaching',
    items: [
      {
        id: 'teaching',
        label: 'Teaching',
        icon: Teacher,
        children: [
          available(
            'teaching-dashboard',
            'My Dashboard',
            '/teaching',
            TEACHER,
            'Today’s classes, work due soon, grading backlog and student alerts.',
          ),
          available(
            'my-classes',
            'My Classes',
            '/teaching/classes',
            TEACHER,
            'The classes you teach: roster, assignments, gradebook and lesson plans.',
          ),
          available(
            'my-schedule',
            'Schedule',
            '/teaching/schedule',
            TEACHER,
            'Your weekly class schedule in school time.',
          ),
          available(
            'lesson-plans',
            'Lesson Planner',
            '/teaching/planner',
            { anyOf: ['lesson_plan.manage'] },
            'Week and month lesson planning with standards alignment.',
          ),
          available(
            'assignments',
            'Assignments',
            '/teaching/assignments',
            { anyOf: ['grades.manage'] },
            'Create, publish and copy assignments.',
          ),
          available(
            'gradebook',
            'Gradebook',
            '/teaching/gradebook',
            { anyOf: ['grades.manage'] },
            'Fast score entry; grades are calculated by the gradebook.',
          ),
          available(
            'teaching-report-cards',
            'Report Cards',
            '/teaching/report-cards',
            { anyOf: ['grades.manage'] },
            'Review calculated grades, add overrides and comments, and submit your classes.',
          ),
          available(
            'student-alerts',
            'Student Alerts',
            '/teaching/alerts',
            { anyOf: ['grades.manage'] },
            'Missing work, failing or at-risk students and ungraded work.',
          ),
          available(
            'teaching-accommodations',
            'Accommodations',
            '/teaching/accommodations',
            { anyOf: ['lesson_plan.manage', 'grades.manage'], allOf: ['accommodations.read'] },
            'Classroom accommodations shared with you for students in your current classes.',
          ),
          {
            // Take attendance for today's class meetings (sections the teacher actively teaches).
            id: 'teaching-attendance',
            label: 'Attendance',
            path: '/teaching/attendance',
            requires: {
              anyOf: ['lesson_plan.manage', 'grades.manage'],
              allOf: ['attendance.take'],
            },
            status: 'available',
            description: 'Take attendance for today’s classes.',
          },
          available(
            'teaching-passes',
            'Hall Passes',
            '/teaching/passes',
            { anyOf: ['passes.issue'] },
            'Issue passes for students in the class you are teaching now; end your active passes.',
          ),
          available(
            'teaching-referrals',
            'Referrals',
            '/teaching/referrals',
            { anyOf: ['discipline.report'] },
            'Refer a student to the discipline office and follow the referral’s status.',
          ),
        ],
      },
    ],
  },
  {
    id: 'people',
    label: 'People',
    items: [
      {
        id: 'people',
        label: 'People',
        icon: People,
        children: [
          {
            // Guardians belong with students; households are managed automatically when guardians
            // are linked, so they have no menu entry (their screens stay reachable by link).
            id: 'students-group',
            label: 'Students',
            children: [
              {
                id: 'students',
                label: 'All students',
                path: '/people/students',
                requires: { anyOf: ['student.read'] },
                status: 'available',
                description: 'Student profiles, demographics and enrollment status.',
              },
              {
                id: 'guardians',
                label: 'Guardians',
                path: '/people/guardians',
                requires: { anyOf: ['guardian.read'] },
                status: 'available',
                description: 'Parents, guardians, custody and contact relationships.',
              },
              {
                id: 'households',
                label: 'Households',
                path: '/people/households',
                requires: { anyOf: ['guardian.read'] },
                status: 'available',
                hidden: true,
                description: 'Households: students and guardians living together.',
              },
            ],
          },
          {
            id: 'teachers',
            label: 'Teachers',
            path: '/people/teachers',
            requires: { anyOf: ['staff.read'] },
            status: 'available',
            description: 'Teacher profiles and school assignments.',
          },
          {
            id: 'staff',
            label: 'Staff',
            path: '/people/staff',
            requires: { anyOf: ['staff.read'] },
            status: 'available',
            description: 'Staff directory, positions and assignments.',
          },
          {
            id: 'officials',
            label: 'Officials',
            path: '/people/officials',
            requires: { anyOf: ['staff.read'] },
            status: 'available',
            description: 'Principals, assistant principals and school officials.',
          },
        ],
      },
    ],
  },
  {
    id: 'academics',
    label: 'Academics',
    items: [
      {
        id: 'academics',
        label: 'Academics',
        icon: Book,
        children: [
          available(
            'courses',
            'Courses',
            '/academics/courses',
            { anyOf: ['course.read'] },
            'Course catalog, credits and prerequisites.',
          ),
          available(
            'classes',
            'Sections',
            '/academics/classes',
            { anyOf: ['class.read'] },
            'Class sections, meeting patterns, teachers and rosters.',
          ),
          available(
            'scheduling',
            'Scheduling',
            '/academics/scheduling',
            { anyOf: ['schedule.read', 'schedule.manage'] },
            'Master schedule and bell schedules (local times per school zone).',
          ),
          available(
            'report-cards',
            'Report Cards',
            '/academics/report-cards',
            REPORT_CARD_ADMIN,
            'Grading periods, teacher submissions, review and publishing of report cards.',
          ),
          available(
            'progress-reports',
            'Progress Reports',
            '/academics/progress-reports',
            REPORT_CARD_ADMIN,
            'Interim progress reports: same workflow as report cards, without credit or GPA.',
          ),
          available(
            'credits',
            'Credits',
            '/academics/credits',
            { anyOf: ['transcript.read', 'transcript.manage'] },
            'Credit summaries, course records, transfer credit and corrections.',
          ),
          available(
            'graduation',
            'Graduation',
            '/academics/graduation',
            RECORDS_CONFIG,
            'Graduation requirement sets and student progress toward them.',
          ),
          available(
            'transcripts',
            'Transcripts',
            '/academics/transcripts',
            { anyOf: ['transcript.read', 'transcript.manage'] },
            'Official and unofficial transcripts and transcript requests.',
          ),
          {
            // Reached from Credits ("GPA policies"); not a menu entry of its own.
            ...available(
              'gpa-policies',
              'GPA Policies',
              '/academics/gpa-policies',
              RECORDS_CONFIG,
              'District GPA policy and school overrides.',
            ),
            hidden: true,
          },
        ],
      },
    ],
  },
  {
    id: 'attendance',
    label: 'Attendance',
    items: [
      {
        id: 'attendance',
        label: 'Attendance',
        icon: TaskSquare,
        children: [
          // The office day view; teachers take attendance under Teaching → Attendance.
          available(
            'daily-attendance',
            'Daily Attendance',
            '/attendance/daily',
            { anyOf: ['attendance.manage', 'attendance.checkin.manage'] },
            'Daily attendance in each school’s local day: exceptions, corrections and bulk.',
          ),
          available(
            'check-in-out',
            'Check-In / Out',
            '/attendance/check-in-out',
            { anyOf: ['attendance.checkin.manage', 'attendance.manage'] },
            'Student arrivals, early checkouts, pickups and returns.',
          ),
          available(
            'attendance-office',
            'Attendance Office',
            '/attendance/office',
            { anyOf: ['attendance.manage', 'attendance.checkin.manage'] },
            'Today’s dashboard, fast event entry and the event timeline.',
          ),
          available(
            'attendance-settings',
            'Attendance Settings',
            '/attendance/settings',
            { anyOf: ['attendance.manage'] },
            'Attendance statuses, absence reasons and daily rules per school.',
          ),
        ],
      },
    ],
  },
  {
    // Front desk + visitors. Student check-in / check-out are attendance office events.
    id: 'front-office',
    label: 'Front Office',
    items: [
      {
        id: 'front-office',
        label: 'Front Office',
        icon: Building,
        children: [
          available(
            'front-office-dashboard',
            'Dashboard',
            '/front-office',
            { anyOf: [...FRONT_OFFICE_READ, 'visitor.read', 'visitor.manage'] },
            'Fast student lookup, today’s counts and front-desk queues.',
          ),
          available(
            'visitors',
            'Visitors',
            '/front-office/visitors',
            { anyOf: ['visitor.read', 'visitor.manage'] },
            'Visitor sign-in, who is on site, approvals and the visitor log.',
          ),
          available(
            'student-calls',
            'Student Calls',
            '/front-office/student-calls',
            { anyOf: FRONT_OFFICE_READ },
            'Call students to the office; their current teacher is notified.',
          ),
          available(
            'student-messages',
            'Messages',
            '/front-office/messages',
            { anyOf: FRONT_OFFICE_READ },
            'Messages for students from families and staff.',
          ),
          available(
            'front-office-appointments',
            'Appointments',
            '/front-office/appointments',
            { anyOf: FRONT_OFFICE_READ },
            'Guardian and visitor appointments in school time.',
          ),
          available(
            'temp-ids',
            'Temporary IDs',
            '/front-office/temp-ids',
            { anyOf: FRONT_OFFICE_READ },
            'Day badges for students without their ID card.',
          ),
          available(
            'transportation-changes',
            'Transportation Changes',
            '/front-office/transportation-changes',
            { anyOf: FRONT_OFFICE_READ },
            'One-day or date-range changes to how students go home.',
          ),
          available(
            'lost-found',
            'Lost & Found',
            '/front-office/lost-found',
            { anyOf: FRONT_OFFICE_READ },
            'Found items, claims and disposal.',
          ),
          available(
            'late-arrivals',
            'Late Students',
            '/front-office/late-arrivals',
            { anyOf: ['attendance.checkin.manage', 'attendance.manage'] },
            'Today’s late arrivals and check-ins.',
          ),
          available(
            'emergency-contacts',
            'Emergency Contacts',
            '/front-office/emergency-contacts',
            { anyOf: FRONT_OFFICE_READ, allOf: ['guardian.read'] },
            'Authorized pickups and emergency contacts for a student.',
          ),
        ],
      },
    ],
  },
  {
    id: 'student-services',
    label: 'Student Services',
    items: [
      {
        id: 'student-services',
        label: 'Student Services',
        icon: ShieldTick,
        children: [
          {
            // Counselor workspace (counseling.read; every read audited). Staff who may only refer
            // (counseling.refer) see just "Refer to Counseling" (status of their own referrals).
            id: 'guidance-group',
            label: 'Guidance',
            children: [
              available(
                'guidance',
                'Dashboard',
                '/student-services/guidance',
                GUIDANCE,
                'Counseling caseloads and plans (sensitive; access is audited).',
              ),
              available(
                'guidance-caseload',
                'My Caseload',
                '/student-services/guidance/caseload',
                GUIDANCE,
                'Students on your caseload with attendance, GPA and credit indicators.',
              ),
              available(
                'guidance-appointments',
                'Appointments',
                '/student-services/guidance/appointments',
                GUIDANCE,
                'Counseling appointments in school time.',
              ),
              available(
                'guidance-referrals',
                'Referral Queue',
                '/student-services/guidance/referrals',
                GUIDANCE,
                'Triage referrals from staff.',
              ),
              available(
                'guidance-interventions',
                'Interventions',
                '/student-services/guidance/interventions',
                GUIDANCE,
                'Tiered interventions (MTSS) and progress monitoring.',
              ),
              available(
                'guidance-follow-ups',
                'Follow-Ups',
                '/student-services/guidance/follow-ups',
                GUIDANCE,
                'Counseling tasks and reminders.',
              ),
              available(
                'guidance-course-requests',
                'Course Requests',
                '/student-services/guidance/course-requests',
                GUIDANCE,
                'Course requests and alternates for the next year.',
              ),
              available(
                'guidance-schedule-changes',
                'Schedule Changes',
                '/student-services/guidance/schedule-changes',
                GUIDANCE,
                'Drop/add requests; apply approved changes to rosters.',
              ),
              available(
                'guidance-scholarships',
                'Scholarships',
                '/student-services/guidance/scholarships',
                GUIDANCE,
                'Scholarship opportunities and applications.',
              ),
              available(
                'counseling-referrals',
                'Refer to Counseling',
                '/student-services/counseling-referrals',
                { anyOf: ['counseling.refer'] },
                'Refer a student to counseling and follow the referral’s status.',
              ),
            ],
          },
          {
            id: 'special-programs-group',
            label: 'Special Programs',
            children: [
              available(
                'special-programs',
                'Dashboard',
                '/student-services/special-programs',
                { anyOf: ['special_programs.read', 'special_programs.manage', 'iep.read'] },
                'Program participation, plans by status and review dates.',
              ),
              available(
                'iep-504',
                'IEP / 504',
                '/student-services/iep-504',
                { allOf: ['student.read', 'iep.read'] },
                'IEP and Section 504 plans (sensitive; access is audited).',
              ),
              available(
                'special-programs-participation',
                'Program Participation',
                '/student-services/special-programs/participation',
                { anyOf: ['special_programs.read', 'special_programs.manage', 'iep.read'] },
                'Students in English learner, gifted, Title I and other programs.',
              ),
            ],
          },
          {
            // Teachers submit referrals under Teaching → Referrals (discipline.report only).
            id: 'discipline-group',
            label: 'Discipline',
            children: [
              available(
                'discipline',
                'Dashboard',
                '/student-services/discipline',
                { anyOf: ['discipline.read'] },
                'Incident counts, open incidents, pending referrals and upcoming detentions.',
              ),
              available(
                'discipline-incidents',
                'Incidents',
                '/student-services/discipline/incidents',
                { anyOf: ['discipline.read'] },
                'Discipline incidents (sensitive; access is audited).',
              ),
              available(
                'discipline-referrals',
                'Referrals',
                '/student-services/discipline/referrals',
                { anyOf: ['discipline.read'] },
                'Review referrals: accept into an incident, merge or decline.',
              ),
              available(
                'detention',
                'Detention',
                '/student-services/discipline/detention',
                { anyOf: ['discipline.read', 'discipline.manage', 'detention.supervise'] },
                'Detention sessions, rosters and supervisor attendance.',
              ),
              available(
                'discipline-settings',
                'Settings',
                '/student-services/discipline/settings',
                { anyOf: ['discipline.manage'] },
                'Severities, categories, locations, action types, workflow and referral form.',
              ),
            ],
          },
          {
            id: 'hall-passes-group',
            label: 'Hall Passes',
            children: [
              available(
                'hall-passes',
                'Active Passes',
                '/student-services/hall-passes',
                { anyOf: ['passes.read'] },
                'Live list of students out on passes, with timers and overdue flags.',
              ),
              available(
                'hall-pass-records',
                'Records & Analytics',
                '/student-services/hall-passes/records',
                { anyOf: ['passes.manage', 'passes.configure'] },
                'Pass history and informational analytics (no automatic consequences).',
              ),
              available(
                'hall-pass-settings',
                'Pass Settings',
                '/student-services/hall-passes/settings',
                { anyOf: ['passes.configure'] },
                'Pass types, destinations, restrictions and limits.',
              ),
            ],
          },
          // Health office: restricted health information (student.health.read; audited).
          available(
            'nurse-office',
            'Nurse Office',
            '/student-services/nurse',
            { allOf: ['student.read', 'student.health.read'] },
            'Health office visits: check-in, daily log, guardian contacts and dispositions (sensitive; access is audited).',
          ),
          {
            // Reached from the Nurse Office tabs; not a menu entry of its own.
            ...available(
              'nurse-visit-reasons',
              'Visit Reasons',
              '/student-services/nurse/reasons',
              { allOf: ['student.health.read', 'student.health.manage'] },
              'Health office visit reason categories.',
            ),
            hidden: true,
          },
          {
            id: 'student-ids',
            label: 'Student IDs',
            path: '/student-services/student-ids',
            requires: { anyOf: ['student_id.manage'] },
            status: 'available',
            description: 'Student ID cards.',
          },
          {
            // Batch printing (reached from the Student IDs page; job detail is routed in router.tsx).
            ...available(
              'student-id-print-jobs',
              'ID Card Print Jobs',
              '/student-services/student-ids/print-jobs',
              { allOf: ['student.read', 'student_id.manage'] },
              'Batch printing of student ID cards.',
            ),
            hidden: true,
          },
        ],
      },
    ],
  },
  {
    id: 'enrollment',
    label: 'Enrollment',
    items: [
      {
        id: 'enrollment',
        label: 'Enrollment',
        icon: Profile2User,
        children: [
          {
            id: 'registration',
            label: 'Registration',
            path: '/enrollment/registration',
            requires: { anyOf: ['enrollment.read'] },
            status: 'available',
            description: 'New student registration.',
          },
          {
            id: 'applications',
            label: 'Applications',
            path: '/enrollment/applications',
            requires: { anyOf: ['enrollment.read'] },
            status: 'available',
            description: 'Enrollment applications and workflow.',
          },
          {
            id: 'forms',
            label: 'Forms',
            path: '/enrollment/forms',
            requires: { anyOf: ['forms.read', 'enrollment.read'] },
            status: 'available',
            description: 'Form builder, versions and submissions.',
          },
          {
            id: 'enrollment-documents',
            label: 'Documents',
            path: '/enrollment/documents',
            requires: { anyOf: ['documents.read'] },
            status: 'available',
            description: 'Enrollment documents.',
            // Superseded by the Documents library (/documents); kept routable for old links.
            hidden: true,
          },
        ],
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        id: 'transportation',
        label: 'Transportation',
        icon: Bus,
        children: [
          available(
            'transportation-dashboard',
            'Overview',
            '/transportation',
            { anyOf: ['transportation.read'] },
            "Today's expected riders, open change requests, exceptions and sync health.",
          ),
          available(
            'live-buses',
            'Live Buses',
            '/transportation/live',
            { anyOf: ['transportation.live.read'] },
            'Live bus locations and on-board counts.',
          ),
          available(
            'driver-console',
            'Driver Console',
            '/transportation/driver',
            { anyOf: ['transportation.operate'] },
            'Start runs, report the bus location and scan students on and off.',
          ),
          available(
            'buses',
            'Buses',
            '/transportation/vehicles',
            { anyOf: ['transportation.read'] },
            'The bus fleet.',
          ),
          available(
            'routes',
            'Routes',
            '/transportation/routes',
            { anyOf: ['transportation.read'] },
            'Bus routes and their default buses.',
          ),
          available(
            'stops',
            'Stops',
            '/transportation/stops',
            { anyOf: ['transportation.read'] },
            'Stops and stop times.',
          ),
          available(
            'student-transportation',
            'Student Transportation',
            '/transportation/students',
            { allOf: ['student.read', 'transportation.read'] },
            'Eligibility, AM/PM route assignments, exceptions and authorized contacts.',
          ),
          available(
            'transportation-exceptions',
            'Exceptions',
            '/transportation/exceptions',
            { allOf: ['student.read', 'transportation.read'] },
            'Temporary ride changes (no bus, parent pickup, another route or stop) across students.',
          ),
          available(
            'transportation-requests',
            'Change Requests',
            '/transportation/change-requests',
            {
              anyOf: [
                'transportation.read',
                'transportation.requests.review',
                'transportation.requests.create',
              ],
            },
            'Transportation change requests: review queue and IntellBus sync status.',
          ),
          available(
            'transportation-settings',
            'Tracking Settings',
            '/transportation/settings',
            { anyOf: ['transportation.manage'] },
            'Live tracking provider and thresholds, route sync flags, rebuild / resync and MyStudentRider links.',
          ),
        ],
      },
      {
        // Cafeteria (LUNCH). Eligibility (free/reduced) is sensitive: it is shown only inside the
        // account drawer to lunch.eligibility.read holders and never at the point of sale.
        id: 'lunch',
        label: 'Lunch Program',
        icon: Coffee,
        children: [
          available(
            'lunch-dashboard',
            'Dashboard',
            '/lunch',
            { anyOf: ['lunch.read', 'lunch.manage'] },
            'Meals served, cafeteria sales and low balances.',
          ),
          available(
            'lunch-pos',
            'Point of Sale',
            '/lunch/pos',
            { anyOf: ['lunch.pos'] },
            'Cafeteria register: scan an ID card, charge meals and à la carte items.',
          ),
          available(
            'lunch-accounts',
            'Accounts',
            '/lunch/accounts',
            { anyOf: ['lunch.read', 'lunch.manage'] },
            'Meal account balances, deposits, ledgers and dietary labels.',
          ),
          available(
            'lunch-menus',
            'Menus',
            '/lunch/menus',
            { anyOf: ['lunch.read', 'lunch.manage', 'lunch.pos'] },
            'Menus by cafeteria location and week.',
          ),
          available(
            'lunch-setup',
            'Setup',
            '/lunch/setup',
            { anyOf: ['lunch.manage'] },
            'Programs, serving windows, prices, items, locations and account rules.',
          ),
        ],
      },
      {
        // Communication Center (COMMS). Message detail (/communications/messages/:id), the
        // Notification Center (/notifications) and notification preferences
        // (/account/notifications) are routed in router.tsx.
        id: 'communications-center',
        label: 'Communications',
        icon: Messages,
        children: [
          available(
            'communications',
            'Messages',
            '/communications',
            { anyOf: [...COMMS_COMPOSE] },
            'Compose, schedule and track messages to students, families and staff.',
          ),
          available(
            'communications-templates',
            'Templates',
            '/communications/templates',
            { anyOf: ['communications.templates.manage'] },
            'Reusable message content per channel with declared variables.',
          ),
          available(
            'communications-groups',
            'Groups',
            '/communications/groups',
            { anyOf: ['communications.send', 'communications.broadcast', 'communications.manage'] },
            'Saved audiences: static lists and dynamic definitions.',
          ),
          available(
            'communications-routing',
            'Event Routing',
            '/communications/routing',
            { anyOf: ['communications.manage'] },
            'Notification routing rules, delivery log and event dispatch.',
          ),
          available(
            'communications-settings',
            'Settings',
            '/communications/settings',
            { anyOf: ['communications.manage'] },
            'Channel providers, broadcast approval, opt-outs and custom groups.',
          ),
        ],
      },
      {
        // Records Department (DOCREC). Cumulative records (/records/students/:id), request detail
        // (/records/requests/:id) and letter print views are routed in router.tsx; the public
        // secure-release page (/records-release) lives outside the authenticated shell.
        id: 'records-department',
        label: 'Records',
        icon: Folder,
        children: [
          available(
            'records',
            'Workspace',
            '/records',
            { anyOf: ['records.read'] },
            'Cumulative records of current and former students (sensitive; access is audited).',
          ),
          available(
            'records-requests',
            'Requests',
            '/records/requests',
            { anyOf: ['records.read', 'records.requests.manage', 'records.release'] },
            'Records requests from intake to release, with the FERPA disclosure log.',
          ),
        ],
      },
      {
        // Documents v2: library (folders, versions, previews) and the retention review queue.
        id: 'documents-library',
        label: 'Documents',
        icon: DocumentText,
        children: [
          available(
            'documents',
            'Library',
            '/documents',
            { anyOf: ['documents.read'] },
            'Folders, versions, previews, metadata and legal holds.',
          ),
          available(
            'documents-retention',
            'Retention Review',
            '/documents/retention',
            { anyOf: ['documents.retention.manage'] },
            'Documents due for disposition: review, extend or approve destruction.',
          ),
        ],
      },
      {
        // Teacher budgets (BUDG). Purchase request detail (/budgets/purchase-requests/:id) is the
        // notification deep link; it is routed in router.tsx.
        id: 'budgets',
        label: 'Teacher Budgets',
        icon: Wallet,
        children: [
          available(
            'budgets-mine',
            'My Budgets',
            '/budgets',
            { anyOf: ['budget.read'] },
            'Classroom, department and school budgets: allocated, committed, spent, remaining.',
          ),
          available(
            'purchase-requests',
            'Purchase Requests',
            '/budgets/purchase-requests',
            { anyOf: ['budget.read', 'budget.submit'] },
            'Purchase requests, approvals and purchasing.',
          ),
          available(
            'budget-approvals',
            'Approvals',
            '/budgets/approvals',
            { anyOf: ['budget.read'] },
            'Purchase requests waiting for your decision.',
          ),
          available(
            'budget-setup',
            'Setup',
            '/budgets/setup',
            { anyOf: ['budget.manage', 'budget.purchasing'] },
            'Fiscal years, categories, vendors and approval workflows.',
          ),
        ],
      },
      // Unified calendar (CAL). Event detail (/calendar/events/:eventId) is the feed/notification
      // deep link; it is routed in router.tsx.
      available(
        'calendar',
        'Calendar',
        '/calendar',
        { anyOf: ['calendar.read'] },
        'District, school, class and personal events with holidays, reporting dates and appointments.',
      ),
      planned(
        'reports',
        'Reports',
        '/reports',
        { anyOf: ['reports.read'] },
        'Operational and compliance reporting.',
      ),
    ].map((item) => ({ ...item, icon: item.icon ?? OPERATION_ICONS[item.id] })),
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      {
        id: 'administration',
        label: 'Administration',
        icon: Setting2,
        children: [
          {
            id: 'admin-schools',
            label: 'Schools',
            path: '/admin/schools',
            requires: { anyOf: ['school.read'] },
            status: 'available',
          },
          {
            id: 'admin-districts',
            label: 'Districts',
            path: '/admin/districts',
            requires: { anyOf: ['school.read'] },
            status: 'available',
          },
          {
            id: 'admin-grade-levels',
            label: 'Grade Levels',
            path: '/admin/grade-levels',
            requires: { anyOf: ['school.read'] },
            status: 'available',
          },
          {
            id: 'admin-academic-years',

            label: 'Academic Years',
            path: '/admin/academic-years',
            requires: { anyOf: ['academic_year.read'] },
            status: 'available',
          },
          {
            id: 'admin-users',
            label: 'Users',
            path: '/admin/users',
            requires: { anyOf: ['user.read'] },
            status: 'available',
          },
          {
            id: 'admin-roles',
            label: 'Roles',
            path: '/admin/roles',
            requires: { anyOf: ['role.read'] },
            status: 'available',
          },
          {
            id: 'admin-invitations',
            label: 'Invitations',
            path: '/admin/invitations',
            requires: { anyOf: ['user.invite'] },
            status: 'available',
          },
          available(
            'admin-integrations',
            'Integrations',
            '/admin/integrations',
            { anyOf: ['integration.read'] },
            'IntellSuite integrations: clients, subscriptions, deliveries, inbox and event catalog.',
          ),
          {
            id: 'admin-settings',
            label: 'Settings',
            path: '/admin/settings',
            requires: { anyOf: ['settings.read', 'organization.read'] },
            status: 'available',
          },
          {
            id: 'admin-audit-logs',
            label: 'Audit Logs',
            path: '/admin/audit-logs',
            requires: { anyOf: ['audit.read'] },
            status: 'available',
          },
        ],
      },
      {
        id: 'platform',
        label: 'Platform',
        icon: Bank,
        path: '/platform/organizations',
        requires: { platformAdmin: true },
        status: 'available',
      },
    ],
  },
];

/** Flattens the tree into leaf routes. */
export function flattenNavigation(sections: AppNavSection[] = NAVIGATION): AppNavItem[] {
  const out: AppNavItem[] = [];
  const walk = (items: AppNavItem[]) => {
    for (const item of items) {
      if (item.children) walk(item.children);
      else if (item.path) out.push(item);
    }
  };
  for (const section of sections) walk(section.items);
  return out;
}

/**
 * The menu: removes hidden items and items the user may not access (and groups/sections left
 * empty). Routes are built from the unfiltered tree, so hidden items stay reachable by URL.
 */
export function filterNavigation(
  sections: AppNavSection[],
  allowed: (requirement: PermissionRequirement | undefined) => boolean,
): AppNavSection[] {
  const filterItems = (items: AppNavItem[]): AppNavItem[] =>
    items.flatMap((item) => {
      if (item.hidden || !allowed(item.requires)) return [];
      if (item.children) {
        const children = filterItems(item.children);
        return children.length > 0 ? [{ ...item, children }] : [];
      }
      return [item];
    });
  return sections
    .map((section) => ({ ...section, items: filterItems(section.items) }))
    .filter((section) => section.items.length > 0);
}
